<template>
  <div style="display:flex; flex-direction:column; width:100%; height:100%; min-height:0;">
    <div v-if="$store.forgeStore.state.loadingToken" 
         class="d-flex flex-column align-center justify-center" 
         style="width:100%; height:100%;">
      <v-progress-circular :size="50" :width="2" color="#2D4B95" indeterminate />
      <div class="mt-4">Cargando Autodesk Forge</div>
    </div>
    <div v-else style="width:100%; height:100%;">
      <div ref="viewerContainer" style="width:100%; height:100%; position:relative;"></div>
    </div>
  </div>
</template>

<script>
const VIEWER_JS_URL = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.min.js'
const VIEWER_CSS_URL = 'https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/style.min.css'

function loadViewerResources() {
  return new Promise((resolve, reject) => {
    if (document.getElementById('forge-viewer-script')) {
      resolve()
      return
    }

    if (!document.getElementById('forge-viewer-css')) {
      const link = document.createElement('link')
      link.id = 'forge-viewer-css'
      link.rel = 'stylesheet'
      link.href = VIEWER_CSS_URL
      document.head.appendChild(link)
    }

    const script = document.createElement('script')
    script.id = 'forge-viewer-script'
    script.src = VIEWER_JS_URL
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('No se pudo cargar el script del viewer de Autodesk'))
    document.head.appendChild(script)
  })
}

export default {
  name: 'Planimetria',
  components: {},
  props: {
    forgeSelectedFile: {
      type: Object,
      default: null
    },
    fetchToken: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    forgeToken() {
      return this.$store.forgeStore.state.forgeToken
    },
    storeForgeSelectedFile() {
      return this.$store.forgeStore.state.forgeSelectedFile
    },
    activeForgeFile() {
      return this.forgeSelectedFile || this.storeForgeSelectedFile
    }
  },
  watch: {
    forgeToken(newData) {
      if (newData && this.activeForgeFile) {
        this.initializeAutodesk()
      }
    },
    activeForgeFile(newData, oldData) {
      if (!newData) {
        this.destroyViewer()
        return
      }
      if (this.forgeToken) {
        if (oldData && oldData.id !== newData.id) {
          this.destroyViewer()
        }
        this.initializeAutodesk()
      }
    }
  },
  data: () => ({
    forgeViewer: null,
    viewerInitialized: false
  }),
  mounted() {
    if (this.fetchToken) {
      this.$store.forgeStore.dispatch('fetchForgeToken', ['data:read', 'bucket:read'])
    }
    if (this.forgeToken && this.activeForgeFile) {
      this.initializeAutodesk()
    }
  },
  beforeDestroy() {
    this.destroyViewer()
  },
  methods: {
    destroyViewer() {
      this.viewerInitialized = false
      if (this.forgeViewer) {
        this.forgeViewer.finish()
        this.forgeViewer = null
        // eslint-disable-next-line no-undef
        Autodesk.Viewing.shutdown()
      }
      if (this.$refs.viewerContainer) {
        this.$refs.viewerContainer.innerHTML = ''
      }
    },

    async initializeAutodesk() {
      if (!this.activeForgeFile) return
      if (this.viewerInitialized) {
        this.setForgeFileToViewer()
        return
      }

      try {
        await loadViewerResources()
        if (!this.activeForgeFile) return
        this.viewerInitialized = true
        this.initializeViewer()
      } catch (error) {
        console.error('Error cargando recursos del viewer:', error)
      }
    },

    initializeViewer() {
      const options = {
        env: 'AutodeskProduction2',
        api: 'streamingV2',
        getAccessToken: this.getForgeToken
      }
      // eslint-disable-next-line no-undef
      if (typeof Autodesk === 'undefined') return

      // eslint-disable-next-line no-undef
      Autodesk.Viewing.Initializer(options, () => {
        if (!this.activeForgeFile || !this.$refs.viewerContainer) return
        const viewerDiv = this.$refs.viewerContainer

        // eslint-disable-next-line no-undef
        this.forgeViewer = new Autodesk.Viewing.Private.GuiViewer3D(viewerDiv, {
          extensions: [
            'Autodesk.DocumentBrowser',
            'Autodesk.Measure',
            'Autodesk.Section',
            'Autodesk.BimWalk',
            'Autodesk.ViewCubeUi',
          ]
        })

        const startResult = this.forgeViewer.start()
        if (startResult > 0) {
          console.error('Error al iniciar el viewer, código:', startResult)
          return
        }

        this.setForgeFileToViewer()
      })
    },

    setForgeFileToViewer() {
      const selectedFile = this.activeForgeFile
      if (!selectedFile?.forge_file?.urn || !this.forgeViewer) return
      const urn = selectedFile.forge_file.urn
      const base64Urn = btoa(urn)

      // eslint-disable-next-line no-undef
      Autodesk.Viewing.Document.load(
        'urn:' + base64Urn,
        (doc) => {
          if (!this.activeForgeFile || this.activeForgeFile.id !== selectedFile.id || !this.forgeViewer) return
          const defaultModel = doc.getRoot().getDefaultGeometry()
          this.forgeViewer.loadDocumentNode(doc, defaultModel).then(() => {
            this.forgeViewer.loadExtension('Autodesk.DocumentBrowser')
            this.forgeViewer.loadExtension('Autodesk.Measure')
            this.forgeViewer.loadExtension('Autodesk.Section')
            this.forgeViewer.loadExtension('Autodesk.BimWalk')
            this.$emit('load')
          })
        },
        (errorCode, errorMessage, messages) => {
          console.error('Error cargando documento:', errorCode, errorMessage, messages)
        }
      )
    },

    getForgeToken(callback) {
      const token = this.$store.forgeStore.state.forgeToken
      callback(token, 3600)
    }
  }
}
</script>